import { getArrowBoundShapeIds } from './arrowSelectConnected.js';
import { selectionItemFromShape } from './selectionItemFromShape.js';
import { findPinsAssociatedWithFrame } from './frameMembership.js';

function boundsContainsPoint(bounds, point) {
  if (!bounds || !point) return false;
  const w = bounds.w ?? 0;
  const h = bounds.h ?? 0;
  return point.x >= bounds.x && point.x <= bounds.x + w
    && point.y >= bounds.y && point.y <= bounds.y + h;
}

function collectChildShapeIds(editor, parentId, into, seen) {
  const childIds = editor.getSortedChildIdsForParent?.(parentId) || [];
  for (const childId of childIds) {
    if (seen.has(childId)) continue;
    const child = editor.getShape?.(childId);
    if (!child) continue;
    seen.add(childId);
    into.push(childId);
    if (child.type === 'frame') collectChildShapeIds(editor, childId, into, seen);
  }
}

/**
 * Shape ids that belong inside a frame: tldraw children, associated pins, and
 * other page-level objects whose center sits in the frame. Connecting lines
 * that bind two already-included objects are included as well.
 */
export function findShapeIdsInsideFrame(editor, frameShape) {
  if (!editor || frameShape?.type !== 'frame') return [];

  const ids = [];
  const seen = new Set([frameShape.id]);

  collectChildShapeIds(editor, frameShape.id, ids, seen);

  const associated = typeof editor.getCurrentPageShapes === 'function'
    ? (findPinsAssociatedWithFrame(editor, frameShape) || [])
    : [];
  for (const entry of associated) {
    const shape = entry?.shape;
    if (!shape?.id || seen.has(shape.id)) continue;
    seen.add(shape.id);
    ids.push(shape.id);
  }

  const frameBounds = editor.getShapePageBounds?.(frameShape);
  const pageId = editor.getCurrentPageId?.();
  for (const shape of editor.getCurrentPageShapes?.() || []) {
    if (!shape?.id || seen.has(shape.id)) continue;
    if (shape.type === 'frame') continue;
    if (pageId && shape.parentId && shape.parentId !== pageId) continue;
    const shapeBounds = editor.getShapePageBounds?.(shape);
    if (!shapeBounds || (shapeBounds.w ?? 0) <= 0 || (shapeBounds.h ?? 0) <= 0) continue;
    const center = {
      x: shapeBounds.x + shapeBounds.w / 2,
      y: shapeBounds.y + shapeBounds.h / 2,
    };
    if (!boundsContainsPoint(frameBounds, center)) continue;
    seen.add(shape.id);
    ids.push(shape.id);
  }

  for (const shape of editor.getCurrentPageShapes?.() || []) {
    if (shape?.type !== 'arrow' || seen.has(shape.id)) continue;
    const bound = getArrowBoundShapeIds(editor, shape.id);
    if (!bound.length || !bound.every(id => seen.has(id))) continue;
    seen.add(shape.id);
    ids.push(shape.id);
  }

  return ids;
}

function resolveShapeForItem(editor, item) {
  if (!editor?.getShape || !item) return null;
  if (item.kind === 'user-pin') {
    for (const shape of editor.getCurrentPageShapes?.() || []) {
      if (shape.type === 'pin-card' && String(shape.meta?.activityId) === String(item.id)) {
        return shape;
      }
    }
    return null;
  }
  return editor.getShape(item.id) || null;
}

/**
 * Expand a canvas selection for Ask AI / copy-as-context:
 * - frame → frame + every object inside it
 * - connecting line → line + bound objects (and their frame contents)
 */
export function expandSelectionItemsForAiContext(
  editor,
  selectionItems = [],
  { activities = [] } = {}
) {
  if (!editor) return [...(selectionItems || [])];

  const result = [];
  const seen = new Set();

  const addItem = (item, { expandFrame = false, expandArrow = false } = {}) => {
    if (!item?.key || seen.has(item.key)) return;
    seen.add(item.key);
    result.push(item);

    const shape = resolveShapeForItem(editor, item);
    if (!shape) return;

    if (expandFrame && shape.type === 'frame') {
      for (const childId of findShapeIdsInsideFrame(editor, shape)) {
        addItem(selectionItemFromShape(editor.getShape(childId), { activities }), {
          expandFrame: true,
          expandArrow: false,
        });
      }
    }

    if (expandArrow && shape.type === 'arrow') {
      for (const boundId of getArrowBoundShapeIds(editor, shape.id)) {
        addItem(selectionItemFromShape(editor.getShape(boundId), { activities }), {
          expandFrame: true,
          expandArrow: false,
        });
      }
    }
  };

  for (const item of selectionItems || []) {
    addItem(item, { expandFrame: true, expandArrow: true });
  }
  return result;
}
